import { doc, onSnapshot, type Firestore } from "firebase/firestore";

export function loadedRamadanId(
  setups: readonly { ramadanId: string }[],
  preferred: string | undefined,
): string | undefined {
  return setups.find((setup) => setup.ramadanId === preferred)?.ramadanId;
}

export function watchOptionalRamadanConfig(input: {
  db: Firestore;
  organizationId: string;
  ramadanId: string;
  kind: "iftarConfig" | "iftarAttendanceConfig";
  resolve: () => Promise<Record<string, unknown>>;
  onData: (data: Record<string, unknown> | undefined) => void;
  onError: (error: Error) => void;
}): () => void {
  let active = true;
  let stop: (() => void) | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const check = async (): Promise<void> => {
    try {
      const result = await input.resolve();
      if (!active) return;
      const field = input.kind === "iftarConfig" ? "sponsorshipExists" : "attendanceExists";
      if (
        result.organizationId !== input.organizationId ||
        result.ramadanId !== input.ramadanId ||
        typeof result[field] !== "boolean"
      )
        throw new Error("ramadan_context_unavailable");
      if (!result[field]) {
        input.onData(undefined);
        // Missing defaults cannot have a Rules-governed listener yet. Retry
        // presence only until first creation; then use Firestore realtime data.
        timer = setTimeout(() => {
          if (active) void check();
        }, 30_000);
        return;
      }
      stop = onSnapshot(
        doc(
          input.db,
          "organizations",
          input.organizationId,
          "ramadan",
          input.ramadanId,
          input.kind,
          input.kind === "iftarConfig" ? "defaults" : "attendance",
        ),
        (snapshot) => {
          if (active) input.onData(snapshot.data());
        },
        (error) => {
          if (active) input.onError(error);
        },
      );
    } catch (error) {
      if (active)
        input.onError(error instanceof Error ? error : new Error("ramadan_context_unavailable"));
    }
  };
  void check();
  return () => {
    active = false;
    clearTimeout(timer);
    stop?.();
  };
}
